// University of Washington (Seattle): public college page (#2743), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UW's Common Data Set 2025-2026 for the Seattle campus
//    (CDS_2025-2026_Seattle.pdf), the file the Office of Planning & Budgeting
//    links from its Common Data Set page. The PDF is served from a cdn.uw.edu
//    storage host, so the SOURCES url is the washington.edu/opb page that
//    links it. C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans.
//  - UW Office of Admissions (admit.washington.edu): First-year (key dates
//    table and "The UW does not participate in early decision or early
//    action admission"), Dates and deadlines, SAT/ACT test scores, Admission
//    to majors, and First-year students by the numbers. The pages carry no
//    entry year; they are the pages as posted for the application that opens
//    August 15, 2026 for autumn 2027.
//  - UW Office of Student Financial Aid, Cost of Attendance, 2026-27
//    resident and non-resident undergraduate budgets.
//  - College Scorecard, school id 236948 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Departures and quirks:
//  - Testing: the admissions page calls UW "technically test-optional"
//    (reviewers do not see scores; high scores may be considered for a
//    handful of students), while CDS C8 marks the SAT and ACT "not considered
//    for admission, even if submitted". The page states the admissions
//    office's policy as Test-optional and quotes the CDS beside it.
//  - Cost: the CDS G1 block checks the box saying final 2026-2027 costs were
//    not available yet (expected 6/1/2026), so the financial aid office's
//    2026-27 budget is used. Its tuition and fees ($13,873 resident, $45,789
//    non-resident) differ from CDS G1 tuition plus required fees ($13,406 and
//    $44,640). The aid page lists miscellaneous costs as $2,508 for residents
//    and $2,506 for non-residents; its non-resident total ($69,129) is $2
//    above the sum of its own items ($69,127), and the items are used here.
//    Food and housing is the aid office's "living on/off campus" allowance;
//    it does not give a separate on-campus figure.
//  - By major: UW publishes three-year average admit rates by residency
//    (overall, engineering Direct to College, computer science and computer
//    engineering Direct to Major), with no counts and no single year, so they
//    are quoted in the note rather than as a dated table.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UW Common Data Set 2025-2026 (Seattle)',
        url: 'https://www.washington.edu/opb/uw-data/external-reporting/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UW Admissions, First-year',
        url: 'https://admit.washington.edu/apply/freshman/',
        read: '2026-10-01',
    },
    dates: {
        label: 'UW Admissions, Dates and deadlines',
        url: 'https://admit.washington.edu/apply/dates-deadlines/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UW Admissions, SAT/ACT scores (first-year)',
        url: 'https://admit.washington.edu/apply/freshman/how-to-apply/test-scores/',
        read: '2026-10-01',
    },
    majorsAdmit: {
        label: 'UW Admissions, Admission to majors',
        url: 'https://admit.washington.edu/apply/admission-to-majors/',
        read: '2026-10-01',
    },
    numbers: {
        label: 'UW Admissions, First-year students by the numbers',
        url: 'https://admit.washington.edu/apply/freshman/by-the-numbers/',
        read: '2026-10-01',
    },
    coa: {
        label: 'UW Student Financial Aid, Cost of Attendance',
        url: 'https://www.washington.edu/financialaid/getting-started/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?236948-University-of-Washington-Seattle-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-washington',
    kbId: 'university_of_washington',
    name: 'University of Washington',
    shortName: 'UW',
    aliases: ['UW', 'UDub'],
    location: 'Seattle, WA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UW brand colors: Husky Purple, Husky Gold); decoration only.
    branding: {
        primary: '#32006E',
        secondary: '#B7A57A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_washington_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 72933,
        admitted: 30446,
        enrolled: 7129,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 15260, admitted: 6980 },
            { group: 'Out-of-state', applied: 44619, admitted: 18813 },
            { group: 'International', applied: 13054, admitted: 4653 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: true,
        note: 'UW publishes three-year average admit rates by residency, not one year\'s counts. Overall: 47% for Washington residents and 39% for nonresidents. Engineering, Direct to College: 43% and 37%. Computer science and computer engineering, Direct to Major: 30% and 2%.',
        source: 'numbers',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UW has no early decision or early action plan. It makes admission decisions only after every first-year application has been reviewed.',
        source: 'firstYear',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most UW students enter their major after they enroll. Some majors admit first-year students directly, and only the major you list first counts for that:',
        rows: [
            { school: 'Paul G. Allen School of Computer Science & Engineering', requirement: 'List computer science or computer engineering as your first-choice major to be considered for Direct to Major admission; listing it second does not count. Most UW computer science and computer engineering students are admitted this way, and UW says getting into these majors later is improbable.' },
            { school: 'College of Engineering', requirement: 'List any engineering major (or engineering undeclared) first to be considered for Direct to College admission. If admitted, you start as engineering undeclared and are guaranteed placement in an engineering major other than computer engineering once you meet the requirements. Getting in later is competitive and space may be limited.' },
            { school: 'Foster School of Business', requirement: 'List Business Administration first to be considered for Direct to College admission. If admitted, you start as a general business major. Entry to Foster after the first year is capacity constrained and not guaranteed.' },
            { school: 'Music', requirement: 'Guitar, jazz studies, music education, orchestral instruments, organ, piano, strings and voice admit first-year students directly, and most music majors also require an audition on your primary instrument.' },
            { school: 'Other direct-admit majors', requirement: 'Architectural studies, biochemistry, construction management, informatics, neuroscience and statistics also admit some students directly as first-years. Outside business, computer science and engineering, only a small share of students enter a major this way.' },
        ],
        note: 'A second-choice major lets UW consider you for general admission if your first choice is full, but it is never used for direct admission.',
        source: 'majorsAdmit',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'November 15' },
        rows: [
            { plan: 'Autumn quarter application', deadline: 'November 15', decision: 'March 1 to 15', binding: false },
        ],
        note: 'The application opens August 15. Test scores, if you send them, are due December 31, and the UW FAFSA and WASFA priority date is February 1. Most admitted students confirm by May 1; your offer letter gives UW\'s own date for the enrollment deposit.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UW permanently dropped the SAT and ACT requirement and calls itself technically test-optional: reviewers do not see your scores when they read your application, and you are not disadvantaged for sending low scores or none. High scores (1400 SAT or 31 ACT and above) may be considered for a handful of students who would not otherwise be admitted. Scores give no advantage for direct admission to computer science or engineering, or for the Honors Program.',
        source: 'tests',
        cdsLine: 'The Common Data Set goes further and marks the SAT and ACT as not considered for admission, even if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13873, 45789] },
            { item: 'Books and course supplies', amounts: [900, 900] },
            { item: 'Food and housing (on or off campus)', amounts: [18858, 18858] },
            { item: 'Transportation', amounts: [1074, 1074] },
            { item: 'Other expenses', amounts: [2508, 2506] },
        ],
        totals: [37213, 69127],
        note: 'The aid office\'s budget for an undergraduate living on or off campus, not with family, for the academic year. Its out-of-state total, $69,129, is $2 more than the sum of its own items, shown here.',
        source: 'coa',
        netPrice: {
            average: 13663,
            byIncome: [
                { income: '$0 to $30,000', amount: 7610 },
                { income: '$30,001 to $48,000', amount: 7112 },
                { income: '$48,001 to $75,000', amount: 8585 },
                { income: '$75,001 to $110,000', amount: 11427 },
                { income: 'Over $110,000', amount: 30441 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UW\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
